'use client';
import { createTheme } from '@mui/material/styles';
import LinkBehaviour from './LinkBehaviour';
import { TypographyVariant } from '@mui/material';
import { getPalette, PalletConfig } from './pallete';

const baseTheme = createTheme();

type BaseFontDefinition = {
  fontSize?: number;
  fontWeight?: string;
  fontFamily?: string;
  lineHeight?: number;
  letterSpacing?: number;
};

type FontDefinition = {
  desktop?: BaseFontDefinition;
  mobile?: BaseFontDefinition;
};

export type GetThemeProps = {
  palette: PalletConfig;
  fonts: {
    [key in TypographyVariant]?: FontDefinition;
  } & {
    default: string;
  };
  shape: {
    borderRadius: number;
    inputBorderRadius?: number;
  };
};

const resolveFont = (
  def?: BaseFontDefinition,
  fontRegistry?: Record<string, string>,
) => {
  if (!def) return {};

  return {
    ...def,
    ...(def.fontFamily && {
      fontFamily: fontRegistry?.[def.fontFamily] || def.fontFamily,
    }),
  };
};

const getTheme = (
  { palette, fonts, shape }: GetThemeProps,
  fontRegistry: Record<string, string>,
  defaultLocale?: string,
) => {
  const inputBorderRadius = shape.inputBorderRadius || shape.borderRadius / 2;
  const defaultFontFamily =
    fontRegistry[fonts.default] || fontRegistry.inter || fonts.default;
  return createTheme({
    palette: getPalette(palette),
    typography: {
      fontFamily: defaultFontFamily,
      h1: {
        fontSize: 68,
        fontWeight: '700',
        ...resolveFont(fonts.h1?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          fontSize: 64,
          ...resolveFont(fonts.h1?.mobile, fontRegistry),
        },
      },
      h2: {
        fontSize: 56,
        fontWeight: '700',
        ...resolveFont(fonts.h2?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          fontSize: 36,
          ...resolveFont(fonts.h2?.mobile, fontRegistry),
        },
      },
      h3: {
        fontSize: 48,
        fontWeight: '500',
        ...resolveFont(fonts.h3?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          fontSize: 32,
          ...resolveFont(fonts.h3?.mobile, fontRegistry),
        },
      },
      h4: {
        fontSize: 32,
        fontWeight: '500',
        ...resolveFont(fonts.h4?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          fontSize: 28,
          ...resolveFont(fonts.h4?.mobile, fontRegistry),
        },
      },
      h5: {
        fontSize: 20,
        fontWeight: '500',
        ...resolveFont(fonts.h5?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.h5?.mobile, fontRegistry),
        },
      },
      h6: {
        fontSize: 16,
        fontWeight: '500',
        ...resolveFont(fonts.h6?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.h6?.mobile, fontRegistry),
        },
      },
      subtitle1: {
        fontWeight: 300,
        fontSize: 20,
        ...resolveFont(fonts.subtitle1?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.subtitle1?.mobile, fontRegistry),
        },
      },
      subtitle2: {
        fontWeight: 300,
        fontSize: 16,
        ...resolveFont(fonts.subtitle2?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.subtitle2?.mobile, fontRegistry),
        },
      },
      body1: {
        ...resolveFont(fonts.body1?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.body1?.mobile, fontRegistry),
        },
      },
      body2: {
        fontSize: '14px',
        lineHeight: 1.5,
        fontWeight: 300,
        ...resolveFont(fonts.body2?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.body2?.mobile, fontRegistry),
        },
      },
      caption: {
        fontSize: '14px',
        lineHeight: 1.0,
        fontWeight: 600,
        ...resolveFont(fonts.caption?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.caption?.mobile, fontRegistry),
        },
      },
      button: {
        fontWeight: 600,
        ...resolveFont(fonts.button?.desktop, fontRegistry),
        [baseTheme.breakpoints.down('md')]: {
          ...resolveFont(fonts.button?.mobile, fontRegistry),
        },
      },
    },
    shape: {
      borderRadius: shape.borderRadius,
    },
    components: {
      MuiLink: {
        defaultProps: {
          component: (props) => (
            <LinkBehaviour defaultLocale={defaultLocale} {...props} />
          ),
          color: 'textPrimary',
          underline: 'hover',
        },
      },
      MuiButtonBase: {
        defaultProps: {
          LinkComponent: (props) => (
            <LinkBehaviour defaultLocale={defaultLocale} {...props} />
          ),
        },
      },
      // START input related
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '16px !important',
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: inputBorderRadius,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      // ENDS input related
      MuiChip: {},
      MuiTooltip: {
        defaultProps: {
          enterTouchDelay: 0,
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              padding: '12px',
            },
          }),
        },
      },
    },
  });
};

export default getTheme;
